/** @jsx jsx */
import { AddIcon, Box, Container, Flex, Heading, Paragraph, SearchIcon, TextInput } from "@eworkit/iris";
import { useBreakpointIndex } from "@theme-ui/match-media";
import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { AppSuspense } from "@/components/app";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { useDebounce } from "@/hooks/Timeout";
import { InviteUserButton } from "@/views/Invitations";
import { ApplyChangeCandidate } from "@/views/job-applications/apply/_candidate/ApplyChangeCandidate";

import { useApplyWizard } from "../_components/ApplyWizardContext";
import { ApplyCandidatesList } from "./ApplyCandidatesList";

interface ApplyWizardCandidateProps {
  jobRequest: JobRequest;
}

export const ApplyWizardCandidate: FC<ApplyWizardCandidateProps> = ({ jobRequest }) => {
  const { t } = useTranslation("session");

  const breakpoint = useBreakpointIndex();

  const { setProfile, goToNextStep, profile, designatedCandidate } = useApplyWizard();

  const [query, setQuery] = useState(designatedCandidate);

  const debouncedQuery = useDebounce(query, 600);

  const handleCandidateAdded = useCallback(
    (profile: IConsultantProfile) => {
      setProfile(profile);
      goToNextStep();
    },
    [setProfile, goToNextStep],
  );

  return profile ? (
    <ApplyChangeCandidate />
  ) : (
    <Flex flexDirection="column">
      <div
        sx={{
          position: "sticky",
          top: [86, null, 62, 70],
          backgroundColor: "n0",
          borderBottom: "primary",
        }}>
        <Container variant="narrow">
          <Box px="sm" py={["sm", null, "md"]} sx={{ pt: ["12px", null, "md"] }}>
            <Flex flexDirection="column" gap={["xs", null, "md"]}>
              <Flex justifyContent="space-between">
                <Flex flexDirection="column">
                  {!designatedCandidate ? (
                    <React.Fragment>
                      <Heading size={["h-sm", null, "h-md"]}>{t("JOB_REQUESTS.APPLY.WIZARD.CANDIDATE")}</Heading>
                      <Paragraph color="n700">{t("JOB_REQUESTS.APPLY.WIZARD.SELECT_CANDIDATE_TO_APPLY")}</Paragraph>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <Heading size={["h-sm", null, "h-md"]}>
                        {t("JOB_REQUESTS.APPLY.WIZARD.DESIGNATED_CANDIDATE")}
                      </Heading>
                      <Paragraph color="n700">
                        {t("JOB_REQUESTS.APPLY.WIZARD.SELECT_DESIGNATED_CANDIDATE_TO_APPLY", {
                          candidate: designatedCandidate,
                        })}
                      </Paragraph>
                    </React.Fragment>
                  )}
                </Flex>

                {breakpoint >= 3 ? (
                  <InviteUserButton
                    size="sm"
                    variant="secondary"
                    iconLeft={<AddIcon />}
                    onSuccess={handleCandidateAdded}>
                    {t("JOB_REQUESTS.APPLY.WIZARD.ADD_CANDIDATE")}
                  </InviteUserButton>
                ) : (
                  <InviteUserButton size="sm" variant="ghost" iconLeft={<AddIcon />} onSuccess={handleCandidateAdded} />
                )}
              </Flex>

              <TextInput
                size="xl"
                icon={<SearchIcon />}
                placeholder={t("JOB_REQUESTS.APPLY.WIZARD.SEARCH_CANDIDATE")}
                value={query}
                onChange={setQuery}
                autoFocus
              />
            </Flex>
          </Box>
        </Container>
      </div>

      <Container variant="narrow">
        <Box px={[null, null, "sm"]} pt={["0", null, "md"]} pb="md">
          <AppSuspense>
            <ApplyCandidatesList
              jobRequest={jobRequest}
              query={debouncedQuery}
              designatedCandidate={designatedCandidate}
            />
          </AppSuspense>
        </Box>
      </Container>
    </Flex>
  );
};
